import { Link, NavLink, Outlet, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api.js";
import { logout } from "../auth.js";
import {
  CalendarIcon,
  ChevronDownIcon,
  ClockIcon,
  MenuIcon,
  PhoneIcon,
  ReceiptIcon,
  SignOutIcon,
  SlidersIcon,
  StoreIcon,
  UserIcon,
  type IconProps
} from "../icons.js";

/**
 * Persistent shell: brand, restaurant switcher, section tabs.
 *
 * The selected restaurant lives in the URL (/r/:restaurantId/...) so links are
 * shareable, the back button works, and every page reads its scope from one place.
 */
const SECTIONS: { to: string; label: string; Icon: (p: IconProps) => JSX.Element; bookingsOnly?: boolean }[] = [
  { to: "orders", label: "Orders", Icon: ReceiptIcon },
  // Only for a restaurant that takes bookings: a tab that is always empty is a tab people learn to ignore.
  { to: "reservations", label: "Reservations", Icon: CalendarIcon, bookingsOnly: true },
  { to: "menu", label: "Menu", Icon: MenuIcon },
  { to: "calls", label: "Calls", Icon: PhoneIcon },
  { to: "hours", label: "Hours", Icon: ClockIcon },
  { to: "info", label: "Details", Icon: StoreIcon },
  { to: "settings", label: "Settings", Icon: SlidersIcon }
];

export function Layout({ email, bookingsAvailable = false }: { email?: string; bookingsAvailable?: boolean }) {
  const { restaurantId = "" } = useParams();

  const mine = useQuery({ queryKey: ["restaurants"], queryFn: () => api.listRestaurants() });
  const owned = mine.data?.restaurants ?? [];

  const profile = useQuery({
    queryKey: ["profile", restaurantId],
    queryFn: () => api.getProfile(restaurantId),
    enabled: Boolean(restaurantId),
    retry: false // a restaurant with no profile row yet is expected, not an error
  });

  // Same query key the Settings page uses, so it is shared rather than fetched twice.
  const settings = useQuery({
    queryKey: ["settings", restaurantId],
    queryFn: () => api.getSettings(restaurantId),
    enabled: Boolean(restaurantId)
  });
  // Both: the stage offers bookings, and this restaurant turned them on.
  const takesBookings = bookingsAvailable && settings.data?.reservations?.enabled === true;

  // Falls back to the id while the profile is missing, so the shell still identifies
  // which restaurant you are in.
  const currentName = profile.data?.displayName ?? restaurantId;

  return (
    <div className="app">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className="topbar">
        <div className="topbar-inner">
          <div className="brand">
            <span className="brand-mark">glowin</span>
          </div>

          <div className="topbar-right">
            {/* The way to change location, for one location or many: the "Choose a location" page, which
                lists every one this person may manage. It used to be a dropdown shown only with two or more,
                so with one there was nothing to click. */}
            <Link
              to="/"
              className="current-restaurant"
              aria-label={`${currentName}, switch location`}
              title="Switch location"
            >
              <StoreIcon size={16} />
              <span className="current-name">{currentName}</span>
              <ChevronDownIcon size={14} />
            </Link>
            <span className="user with-icon" title={email}>
              <UserIcon size={16} />
              <span>{email}</span>
            </span>
            <button className="ghost with-icon" onClick={() => void logout()} aria-label="Sign out" title="Sign out">
              <SignOutIcon size={16} />
              <span className="signout-label">Sign out</span>
            </button>
          </div>
        </div>

        <nav className="tabs" aria-label="Sections">
          {SECTIONS.filter((s) => !s.bookingsOnly || takesBookings).map((s) => (
            <NavLink
              key={s.to}
              to={`/r/${restaurantId}/${s.to}`}
              className={({ isActive }) => (isActive ? "tab active" : "tab")}
            >
              <s.Icon size={16} />
              <span>{s.label}</span>
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="content" id="main" tabIndex={-1}>
        {owned.length > 1 && (
          <p className="scope-note">
            Editing <strong>{currentName}</strong>
          </p>
        )}
        <Outlet />
      </main>
    </div>
  );
}
